"use client";
/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities */

import { useState, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { CreditCard, Eye, EyeOff, ShoppingBag, Tag, User, Receipt, ShieldCheck, Zap, BadgeCheck } from "lucide-react";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant, useServiceFeeBps } from "@/components/tenant/TenantProvider";
import { checkoutApi, funnelsApi } from "@/lib/api-client";
import { formatRupiah } from "@/lib/utils";
import { PAYMENT_METHODS, PAYMENT_ADMIN_FEE_MAX, PAYMENT_ADMIN_FEE_MIN, recommendedMethod, type PaymentMethodId } from "@/lib/payment-methods";
import { FunnelBumpCard, type BumpOffer } from "@/components/checkout/FunnelBumpCard";
import { useTurnstile, isTurnstileError } from "@/hooks/use-turnstile";
import { track, trackPageView } from "@/lib/tracking/client";
import { pathSegments } from "@/lib/path-slug";

interface ProductVariant {
  id: string;
  name: string;
  price: number;
  compare_at_price?: number | null;
}

/** Item yang termasuk dalam bundling (ditampilkan sebagai daftar isi paket). */
interface BundleItem {
  item_type: "digital_product" | "course";
  item_id: string;
  name: string;
  thumbnail_url?: string | null;
}

interface Product {
  id: string;
  name: string;
  price: number;
  compare_at_price?: number;
  type: "course" | "membership" | "digital_product" | "bundle";
  description?: string;
  access_type?: string;
  slug?: string;
  thumbnail_url?: string | null;
  /** Produk digital dengan varian → pembeli wajib memilih paket. */
  variants?: ProductVariant[];
  /** Hanya untuk type 'bundle'. */
  items?: BundleItem[];
  /** 'pwyw' = bayar seikhlasnya (pay what you want). Absen = fixed. */
  pricing_mode?: "fixed" | "pwyw";
  /** Nominal minimal untuk pwyw. */
  min_price?: number;
  /** Stok habis → tombol beli dinonaktifkan. */
  sold_out?: boolean;
  /** Jendela penjualan (ISO). */
  available_from?: string | null;
  available_until?: string | null;
}

// Plans whose promo is built into the price (compare_at_price → price). For
// these we show the promo code as branding and HIDE the manual coupon input so
// the deep promo can't be stacked. The matching coupon records are kept inactive.
const PLAN_PROMO_CODE: Record<string, string> = {
  yearly: "TAHUNAN",
  lifetime: "LIFETIME",
};

interface UserInfo {
  name: string;
  email: string;
  phone: string | null;
}

export default function OrderPageClient() {
  const { brand_name } = useTenant();
  const feeRateBps = useServiceFeeBps();
  const pathname = usePathname();
  const router = useRouter();
  const productSlug = pathSegments(pathname)[1] ?? "_";

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useRef<HTMLDivElement>(null);
  const [refCode, setRefCode] = useState("");
  const [bump, setBump] = useState<BumpOffer | null>(null);
  const [bumpSelected, setBumpSelected] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodId>("qris");
  const [selectedVariantId, setSelectedVariantId] = useState<string>("");

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [idempotencyKey] = useState(() => typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
  // For logged-in users missing phone
  const [mobile, setMobile] = useState("");

  // Guest checkout form
  const [guestForm, setGuestForm] = useState({ name: "", email: "", phone: "", password: "", _hp_website: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateField, setDuplicateField] = useState<"email" | "phone" | null>(null);
  const turnstile = useTurnstile("turnstile-spin-v2");

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponOpen, setCouponOpen] = useState(false);
  const [autoCoupon, setAutoCoupon] = useState("");
  const [couponState, setCouponState] = useState<{ status: 'idle' | 'loading' | 'valid' | 'invalid', error?: string, discount_type?: string, discount_value?: number, max_discount_amount?: number | null }>({ status: 'idle' });

  // PWYW (bayar seikhlasnya) — string angka polos (tanpa titik), diformat saat render
  const [customAmountRaw, setCustomAmountRaw] = useState("");
  const [customAmountTouched, setCustomAmountTouched] = useState(false);

  // Scroll pesan error ke tampilan begitu muncul — CTA ada di sticky bar bawah,
  // jadi tanpa ini siswa HP tak melihat alasan submit gagal.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error]);

  // Read ?ref= from URL and track click; ?coupon= → prefill + auto-validate
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || "";
    if (ref) {
      setRefCode(ref);
      fetch("/api/affiliate/track-click", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ affiliate_code: ref, landing_url: window.location.pathname }),
      }).catch(() => {});
    }
    const coupon = (params.get("coupon") || "").trim().toUpperCase();
    if (coupon) {
      setCouponInput(coupon);
      setCouponOpen(true);
      setAutoCoupon(coupon);
    }
  }, []);

  // First-party tracking: order page entry = upgrade intent signal
  useEffect(() => {
    if (!productSlug || productSlug === "_") return;
    trackPageView("order");
    track("checkout_intent", { metadata: { product_slug: productSlug } });
  }, [productSlug]);

  // Auth check
  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) {
          setIsLoggedIn(false);
        } else {
          const u = d.data?.user || d.data;
          setIsLoggedIn(true);
          setUser({ name: u.name || "", email: u.email || "", phone: u.phone || null });
        }
      })
      .catch(() => setIsLoggedIn(false));
  }, [productSlug]);

  // Fetch product info
  useEffect(() => {
    if (!productSlug || productSlug === "_") return;

    const isMembership = productSlug.startsWith("membership-") || productSlug.endsWith("-promo") || ["monthly", "yearly", "lifetime"].includes(productSlug);
    if (isMembership) {
      const planSlug = productSlug.startsWith("membership-")
        ? productSlug.replace("membership-", "")
        : productSlug;
      fetch("/api/membership/plans", { credentials: "include" })
        .then((r) => r.json())
        .then((d) => {
          const plan = (d.data || []).find((p: { slug: string }) => p.slug === planSlug);
          if (plan) {
            setProduct({
              id: plan.id,
              name: plan.name,
              price: plan.price,
              compare_at_price: plan.compare_at_price,
              type: "membership",
              description: plan.description,
              slug: plan.slug,
            });
          }
          setLoading(false);
        });
    } else {
      fetch("/api/courses", { credentials: "include" })
        .then((r) => r.json())
        .then(async (d) => {
          const course = (d.data || []).find((c: { slug: string }) => c.slug === productSlug);
          if (course) {
            setProduct({
              id: course.id,
              name: course.title,
              price: course.price,
              type: "course",
              description: course.short_description,
              access_type: course.access_type,
              slug: course.slug,
            });
            setLoading(false);
            return;
          }
          // Fallback 1: produk digital (tripwire/ebook/template)
          try {
            const dpRes = await fetch(`/api/public/digital-products/${productSlug}`, { credentials: "include" });
            const dp = await dpRes.json();
            if (dp.success && dp.data) {
              // Endpoint publik boleh menyertakan `variants`; bila absen → produk harga tunggal.
              const variants: ProductVariant[] = Array.isArray(dp.data.variants) ? dp.data.variants : [];
              setProduct({
                id: dp.data.id,
                name: dp.data.name,
                price: dp.data.price,
                compare_at_price: dp.data.compare_at_price || undefined,
                type: "digital_product",
                description: dp.data.description || undefined,
                slug: dp.data.slug,
                thumbnail_url: dp.data.thumbnail_url || undefined,
                ...(variants.length > 0 ? { variants } : {}),
                // Kontrak baru (pwyw/stok/jendela) — toleran bila API belum mengirim
                pricing_mode: dp.data.pricing_mode === "pwyw" ? "pwyw" : "fixed",
                min_price: typeof dp.data.min_price === "number" ? dp.data.min_price : undefined,
                sold_out: dp.data.sold_out === true,
                available_from: dp.data.available_from || null,
                available_until: dp.data.available_until || null,
              });
              setLoading(false);
              return;
            }
          } catch { /* lanjut ke fallback bundling */ }

          // Fallback 2: bundling (paket berisi beberapa produk/kelas)
          try {
            const bRes = await fetch(`/api/public/product-bundles/${productSlug}`, { credentials: "include" });
            const b = await bRes.json();
            if (b.success && b.data) {
              setProduct({
                id: b.data.id,
                name: b.data.name,
                price: b.data.price,
                compare_at_price: b.data.compare_at_price || undefined,
                type: "bundle",
                description: b.data.description || undefined,
                slug: b.data.slug,
                thumbnail_url: b.data.thumbnail_url || undefined,
                items: Array.isArray(b.data.items) ? b.data.items : [],
                sold_out: b.data.sold_out === true,
                available_from: b.data.available_from || null,
                available_until: b.data.available_until || null,
              });
            }
          } catch { /* produk tidak ditemukan */ }
          setLoading(false);
        });
    }
  }, [productSlug]);

  // Order bump dari funnel aktif (menggantikan bump konsultasi hardcoded)
  useEffect(() => {
    setBump(null);
    setBumpSelected(false);
    if (!product) return;
    funnelsApi.getBump(product.type, product.id).then((res) => {
      if (res.success && res.data) setBump(res.data as BumpOffer);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Default metode pembayaran: Annual/Lifetime → VA, lainnya → QRIS
  useEffect(() => {
    if (product) setPaymentMethod(recommendedMethod(product.type, product.slug || productSlug));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Varian pertama dipilih otomatis agar harga & tombol bayar selalu punya acuan.
  useEffect(() => {
    setSelectedVariantId(product?.variants?.[0]?.id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  /** Validasi kupon ke endpoint publik — kontrak: {code, order_type, product_id|plan_id}. */
  const validateCoupon = async (codeArg?: string) => {
    const code = (codeArg ?? couponInput).trim().toUpperCase();
    if (!code || !product) return;
    setCouponState({ status: 'loading' });
    try {
      const res = await fetch('/api/public/coupons/validate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          order_type: product.type,
          ...(product.type === "membership" ? { plan_id: product.id } : { product_id: product.id }),
        }),
      });
      const data = await res.json();
      const d = data?.data as { valid?: boolean; discount_type?: string; discount_value?: number; max_discount_amount?: number | null; reason?: string } | undefined;
      if (data.success && d && d.valid !== false) {
        setCouponState({
          status: 'valid',
          discount_type: d.discount_type,
          discount_value: d.discount_value,
          max_discount_amount: d.max_discount_amount,
        });
        setCouponCode(code);
      } else {
        setCouponState({ status: 'invalid', error: d?.reason || data.error || 'Kode tidak valid atau sudah tidak berlaku' });
      }
    } catch {
      setCouponState({ status: 'invalid', error: 'Gagal mengecek kode. Periksa koneksi lalu coba lagi.' });
    }
  };

  // ?coupon= dari URL → auto-validate begitu produk termuat
  useEffect(() => {
    if (!autoCoupon || !product) return;
    setAutoCoupon("");
    validateCoupon(autoCoupon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCoupon, product?.id]);

  /** Pixel iklan (fbq/ttq di-inject per-tenant oleh worker) — selalu guarded. */
  const firePixelEvent = (fbEvent: string, ttEvent: string, value: number) => {
    try {
      const w = window as unknown as {
        fbq?: (...args: unknown[]) => void;
        ttq?: { track?: (...args: unknown[]) => void };
      };
      w.fbq?.("track", fbEvent, { value, currency: "IDR" });
      w.ttq?.track?.(ttEvent, { value, currency: "IDR" });
    } catch { /* pixel tidak terpasang */ }
  };

  const getAffiliateCookie = () =>
    document.cookie
      .split(";")
      .find((c) => c.trim().startsWith("affiliate_code="))
      ?.split("=")[1]
      ?.trim() || refCode || "";

  /** id varian yang dikirim ke create-payment; undefined bila produk tanpa varian. */
  const resolveVariantId = () => {
    const vs = product?.variants ?? [];
    if (vs.length === 0) return undefined;
    return (vs.find((v) => v.id === selectedVariantId) ?? vs[0]).id;
  };

  // Logged-in user: paid order
  const handleOrder = async () => {
    if (!product) return;
    const chosenVariantId = resolveVariantId();
    const needsMobile = !user?.phone;
    if (needsMobile && !mobile.trim()) {
      setError("Nomor WhatsApp wajib diisi");
      return;
    }
    setOrdering(true);
    setError("");

    const res = await checkoutApi.createPayment({
      order_type: product.type,
      product_id: product.id,
      payment_method: paymentMethod,
      affiliate_code: getAffiliateCookie() || undefined,
      coupon_code: couponState.status === 'valid' && couponCode ? couponCode : undefined,
      ...(mobile.trim() ? { mobile: mobile.trim() } : {}),
      ...(chosenVariantId ? { variant_id: chosenVariantId } : {}),
      ...(isPwyw ? { custom_amount: customAmount } : {}),
      ...(bump && bumpSelected ? { addons: [{ type: "funnel_bump", step_id: bump.step_id }] } : {}),
    }, idempotencyKey);

    handleCreatePaymentResult(res);
  };

  /** Respons create-payment: {free:true} → langsung sukses; selain itu → halaman bayar. */
  const handleCreatePaymentResult = (res: { success: boolean; data?: unknown; error?: string }) => {
    const d = res.data as { order_id?: string; free?: boolean; order_page_url?: string } | undefined;
    if (res.success && d?.free) {
      const dest = d.order_page_url || (d.order_id ? `/checkout/success?order_id=${d.order_id}` : "/dashboard");
      window.location.href = dest;
      return;
    }
    if (res.success && d?.order_id) {
      firePixelEvent("InitiateCheckout", "InitiateCheckout", displayTotal);
      router.push(`/checkout/${d.order_id}`);
    } else {
      setError(res.error || "Gagal membuat order. Silakan coba lagi.");
      setOrdering(false);
    }
  };

  // Logged-in user: free enroll
  const handleFreeEnroll = async () => {
    if (!product) return;
    setOrdering(true);
    const res = await fetch("/api/user/enroll/free", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ course_id: product.id }),
    });
    const d = await res.json();
    if (d.success) {
      router.push(`/learn/${product.slug || productSlug}/start`);
    } else {
      setError(d.error || "Gagal mendaftar. Silakan coba lagi.");
      setOrdering(false);
    }
  };

  // Guest: register + order/enroll in one step
  const handleGuestOrder = async () => {
    if (!product) return;
    const { name, email, phone, password } = guestForm;
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError("Nama, email, dan WhatsApp wajib diisi");
      return;
    }
    // Password OPSIONAL (guest checkout). Bila diisi, harus valid & cocok.
    if (password) {
      if (password.length < 8) {
        setError("Password minimal 8 karakter");
        return;
      }
      if (password !== confirmPassword) {
        setError("Password tidak cocok");
        return;
      }
    }
    setOrdering(true);
    setError("");
    setIsDuplicate(false);
    setDuplicateField(null);

    // Step 1: Register (skip-OTP — efek samping order, bukan signup langsung) — sets session cookie
    const regResponse = await fetch("/api/auth/register-guest", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: name.trim(), email: email.trim(), phone: phone.trim(), ...(password ? { password } : {}), cfTurnstileResponse: turnstile.token }),
    });
    const regRes = await regResponse.json();

    if (!regRes.success) {
      // Token Turnstile SEKALI PAKAI dan server memverifikasinya SEBELUM cek
      // duplikat — jadi token ini sudah mati apa pun alasan gagalnya. Retry
      // tanpa reset pasti kena `timeout-or-duplicate`, dan pengguna terjebak
      // "Validasi keamanan gagal" sampai halaman di-reload.
      if (isTurnstileError(regRes.errorType)) {
        turnstile.noteServerRejection(regRes.errorType);
        setError(regRes.error);
      } else {
        turnstile.reset();
        if (regResponse.status === 409) {
          setIsDuplicate(true);
          setDuplicateField((regRes.error as string)?.includes("WhatsApp") ? "phone" : "email");
        } else {
          setError(regRes.error || "Gagal membuat akun. Coba lagi.");
        }
      }
      setOrdering(false);
      return;
    }

    // Step 2: Free → enroll, paid → create order (session now active via cookie)
    if (isFree) {
      const res = await fetch("/api/user/enroll/free", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ course_id: product.id }),
      }).then((r) => r.json());
      if (res.success) {
        router.push(`/learn/${product.slug || productSlug}/start`);
      } else {
        setError(res.error || "Gagal mendaftar.");
        setOrdering(false);
      }
      return;
    }

    const res = await checkoutApi.createPayment({
      order_type: product.type,
      product_id: product.id,
      payment_method: paymentMethod,
      affiliate_code: getAffiliateCookie() || undefined,
      coupon_code: couponState.status === 'valid' && couponCode ? couponCode : undefined,
      mobile: guestForm.phone.trim(),
      ...(resolveVariantId() ? { variant_id: resolveVariantId() } : {}),
      ...(isPwyw ? { custom_amount: customAmount } : {}),
      ...(bump && bumpSelected ? { addons: [{ type: "funnel_bump", step_id: bump.step_id }] } : {}),
    }, idempotencyKey);
    handleCreatePaymentResult(res);
  };

  const durationLabel: Record<string, string> = {
    "membership-monthly": "per bulan",
    "membership-yearly": "per tahun",
    "membership-lifetime": "sekali bayar, akses selamanya",
    "monthly": "per bulan",
    "yearly": "per tahun",
    "lifetime": "sekali bayar, akses selamanya",
  };

  if (loading || isLoggedIn === null) {
    return (
      <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-4">
          <div className="rounded-2xl border border-[rgba(12,12,10,0.08)] bg-white p-6 shadow-sm">
            <div className="mx-auto h-8 w-28 skeleton rounded" />
            <div className="mt-6 h-4 w-24 skeleton rounded" />
            <div className="mt-3 h-6 w-3/4 skeleton rounded" />
            <div className="mt-2 h-4 w-full skeleton rounded" />
            <div className="mt-6 space-y-2">
              <div className="h-11 w-full skeleton rounded-xl" />
              <div className="h-11 w-full skeleton rounded-xl" />
            </div>
          </div>
          <div className="h-14 w-full skeleton rounded-2xl" />
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main id="main-content" tabIndex={-1} className="flex min-h-dvh items-center justify-center bg-[var(--surface,#F7F7F8)] px-5">
        <div className="w-full max-w-sm rounded-2xl border border-black/[0.07] bg-white p-8 text-center shadow-[0_10px_40px_rgba(0,0,0,0.06)]">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--brand-primary-soft,#EFF4FF)]">
            <ShoppingBag className="h-6 w-6 text-[var(--brand-primary)]" />
          </div>
          <p className="text-base font-bold text-warm-black">Produk tidak ditemukan</p>
          <p className="mx-auto mt-1.5 max-w-xs text-sm leading-relaxed text-gray-500">
            Link produk ini mungkin salah, kedaluwarsa, atau produknya sudah tidak tersedia.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <Link
              href="/store"
              className="inline-flex h-11 items-center justify-center rounded-xl bg-[var(--brand-primary)] px-4 text-sm font-semibold text-[var(--brand-on-primary,#fff)] transition active:scale-[0.98]"
            >
              Lihat produk lainnya
            </Link>
            <Link
              href="/dashboard"
              className="inline-flex h-11 items-center justify-center rounded-xl px-4 text-sm font-medium text-gray-500 transition hover:bg-gray-50"
            >
              Kembali ke Dashboard
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const isFree = product.type === "course" && product.access_type === "free";
  const needsMobile = isLoggedIn && !user?.phone;

  // ── PWYW (bayar seikhlasnya) ──
  const isPwyw = product.pricing_mode === "pwyw";
  const pwywMin = Math.max(product.min_price ?? 0, 5000);
  const customAmount = parseInt(customAmountRaw || "0", 10) || 0;
  const pwywInvalid = isPwyw && customAmount < pwywMin;
  const pwywError =
    isPwyw && customAmountTouched && pwywInvalid ? `Minimal ${formatRupiah(pwywMin)}` : "";

  // ── Stok & jendela penjualan ──
  const nowMs = Date.now();
  const fromMs = product.available_from ? new Date(product.available_from).getTime() : NaN;
  const untilMs = product.available_until ? new Date(product.available_until).getTime() : NaN;
  const notYetOpen = Number.isFinite(fromMs) && nowMs < fromMs;
  const salesClosed = Number.isFinite(untilMs) && nowMs > untilMs;
  const soldOut = !!product.sold_out;
  const purchaseBlocked = !isFree && (soldOut || notYetOpen || salesClosed);
  const formatWib = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const tgl = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Jakarta" }).format(d);
    const jam = new Intl.DateTimeFormat("id-ID", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" }).format(d).replace(".", ":");
    return `${tgl} ${jam} WIB`;
  };
  const blockedReason = soldOut
    ? "Stok produk ini sudah habis."
    : notYetOpen
    ? `Penjualan dibuka ${formatWib(product.available_from!)}.`
    : salesClosed
    ? "Penjualan produk ini sudah ditutup."
    : "";

  // Harga yang ditampilkan mengikuti varian terpilih; tanpa varian → harga utama produk.
  // PWYW: harga = nominal yang diketik pembeli.
  const variants = product.variants ?? [];
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) ?? variants[0];
  const effectivePrice = isPwyw ? customAmount : selectedVariant?.price ?? product.price;
  const effectiveCompareAt = isPwyw
    ? undefined
    : selectedVariant
    ? selectedVariant.compare_at_price ?? undefined
    : product.compare_at_price;

  const removeCoupon = () => {
    setCouponInput("");
    setCouponCode("");
    setCouponState({ status: 'idle' });
  };

  const readOnlyInputClass =
    "w-full px-3.5 py-2.5 text-sm rounded-lg border border-[rgba(12,12,10,0.08)] bg-[rgba(12,12,10,0.03)] text-warm-black cursor-default select-none";
  const editableInputClass =
    "w-full px-3.5 py-2.5 text-sm rounded-lg border border-[rgba(12,12,10,0.12)] bg-white text-warm-black outline-none focus:border-primary transition-colors";

  const hasDiscount = !!(effectiveCompareAt && effectiveCompareAt > effectivePrice);
  const promoCode = product.slug ? PLAN_PROMO_CODE[product.slug] : undefined;
  const discountPct = hasDiscount
    ? Math.round(((effectiveCompareAt! - effectivePrice) / effectiveCompareAt!) * 100)
    : 0;

  // Calculate coupon discount
  let couponDiscountAmount = 0;
  if (couponState.status === 'valid' && couponState.discount_value) {
    if (couponState.discount_type === 'percentage') {
      couponDiscountAmount = Math.floor(effectivePrice * (couponState.discount_value / 100));
      if (couponState.max_discount_amount && couponDiscountAmount > couponState.max_discount_amount) {
        couponDiscountAmount = couponState.max_discount_amount;
      }
    } else {
      couponDiscountAmount = couponState.discount_value;
    }
    if (couponDiscountAmount > effectivePrice) couponDiscountAmount = effectivePrice;
  }

  const bumpAmount = bump && bumpSelected ? bump.amount : 0;
  const subtotalAfterDiscount = effectivePrice - couponDiscountAmount + bumpAmount;
  // Biaya layanan buyer-borne = round(subtotal × bps/10000), ditambahkan di atas.
  // Cocok dengan perhitungan server (orders/service.ts) atas subtotal+bump.
  const serviceFee = Math.round((subtotalAfterDiscount * feeRateBps) / 10000);
  const displayTotal = subtotalAfterDiscount + serviceFee;
  // Produk berbayar dengan total 0 (harga 0 / kupon 100%) → alur "Dapatkan Gratis":
  // tanpa picker metode bayar, create-payment membalas {free:true, order_page_url}.
  const totalIsZero = !isFree && !isPwyw && subtotalAfterDiscount <= 0;

  // ── CTA bersama: dipakai footer inline & sticky pay bar (mobile) ──
  const primaryAction =
    isLoggedIn === false ? handleGuestOrder : isFree ? handleFreeEnroll : handleOrder;
  // Checkout tamu wajib menunggu token Turnstile. Sebelumnya form bisa dikirim
  // dengan token kosong (widget belum selesai / gagal muat), server menolaknya,
  // dan pengguna hanya melihat "Gagal membuat akun. Coba lagi." — selamanya.
  const turnstileRequired = isLoggedIn === false && turnstile.required;
  const turnstilePending = turnstileRequired && turnstile.pending;
  const primaryDisabled =
    ordering || purchaseBlocked || (isPwyw && pwywInvalid) || turnstilePending;
  const ctaLabel = purchaseBlocked
    ? soldOut
      ? "Stok Habis"
      : notYetOpen
      ? "Belum Dibuka"
      : "Penjualan Ditutup"
    : turnstilePending
    ? "Memverifikasi keamanan…"
    : totalIsZero
    ? isLoggedIn === false
      ? "Daftar & Dapatkan Gratis →"
      : "Dapatkan Gratis →"
    : isLoggedIn === false
    ? isFree
      ? "Daftar & Mulai →"
      : "Daftar & Bayar →"
    : isFree
    ? "Daftar Gratis →"
    : "Bayar Sekarang →";
  const ctaSpinner = (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-dvh bg-warm-cream flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(12,12,10,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(12,12,10,0.04) 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      }}
    >
      {/* JSON-LD Product — berdampingan dengan Organization di <head> (tenant.ts),
          bukan pengganti. Halaman ini client-only (static export) jadi baru masuk
          DOM pasca-hydration; tetap berguna untuk crawler yang mengeksekusi JS. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.description,
            image: product.thumbnail_url || undefined,
            offers: {
              "@type": "Offer",
              price: product.price,
              priceCurrency: "IDR",
              availability: product.sold_out
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
            },
          }).replace(/</g, "\\u003c"),
        }}
      />
      <div className="w-full max-w-md space-y-3 pb-24 md:pb-0">
        {/* ── Main card ── */}
        <div className="bg-white rounded-2xl border border-[rgba(12,12,10,0.08)] shadow-sm overflow-hidden">
          <div className="h-1 bg-primary w-full" />

          {/* Logo */}
          <div className="flex justify-center px-6 pt-6 pb-5">
            <BrandLogo width={128} height={32} priority className="h-8 w-auto" />
          </div>

          {/* ── Section: Produk ── */}
          <div className="h-px bg-black/[0.06]" />
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag className="h-4 w-4" style={{ color: "rgba(12,12,10,0.62)" }} />
              <span
                className="text-[11px] font-semibold tracking-widest uppercase"
                style={{ color: "rgba(12,12,10,0.62)" }}
              >
                {product.type === "membership"
                  ? "Membership"
                  : product.type === "digital_product"
                  ? "Produk Digital"
                  : product.type === "bundle"
                  ? "Bundling"
                  : "Kelas"}
              </span>
            </div>
            {isFree ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <p className="text-sm font-semibold text-green-700">{product.name}</p>
                <p className="text-xs text-green-600 mt-0.5">Kelas Gratis — daftar tanpa biaya apapun</p>
              </div>
            ) : (
              <div>
                {product.type === "bundle" && product.thumbnail_url && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={product.thumbnail_url}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    className="mb-3 h-32 w-full rounded-xl object-cover"
                  />
                )}
                <p className="text-base font-semibold text-warm-black">{product.name}</p>
                {product.description && (
                  <p className="text-xs mt-1 leading-relaxed" style={{ color: "rgba(12,12,10,0.65)" }}>
                    {product.description}
                  </p>
                )}

                {/* Status stok / jendela penjualan */}
                {purchaseBlocked && (
                  <div className="mt-2.5">
                    {soldOut ? (
                      <span className="inline-flex items-center rounded-md bg-gray-100 border border-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-600">
                        Stok habis
                      </span>
                    ) : notYetOpen ? (
                      <span className="inline-flex items-center rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-700">
                        Penjualan dibuka {formatWib(product.available_from!)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-md bg-gray-100 border border-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-600">
                        Penjualan sudah ditutup
                      </span>
                    )}
                  </div>
                )}

                {/* Isi bundling */}
                {product.type === "bundle" && (product.items?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <p
                      className="text-[11px] font-semibold tracking-widest uppercase mb-1.5"
                      style={{ color: "rgba(12,12,10,0.62)" }}
                    >
                      Isi Paket
                    </p>
                    <ul className="space-y-1">
                      {product.items!.map((it) => (
                        <li
                          key={`${it.item_type}-${it.item_id}`}
                          className="flex items-center gap-2 text-xs text-warm-black"
                        >
                          <span className="text-primary">✓</span>
                          <span className="min-w-0 flex-1 truncate">{it.name}</span>
                          <span
                            className="shrink-0 rounded-md border border-[rgba(12,12,10,0.08)] px-1.5 py-0.5 text-[11px]"
                            style={{ color: "rgba(12,12,10,0.62)" }}
                          >
                            {it.item_type === "course" ? "Kelas" : "Produk Digital"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Bayar seikhlasnya (PWYW) — input nominal + chip saran */}
                {isPwyw && !purchaseBlocked && (
                  <div className="mt-4">
                    <p
                      className="text-[11px] font-semibold tracking-widest uppercase mb-2"
                      style={{ color: "rgba(12,12,10,0.62)" }}
                    >
                      Bayar Seikhlasnya
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2.5">
                      {[pwywMin, pwywMin * 2, pwywMin * 5].map((amt) => {
                        const active = customAmount === amt;
                        return (
                          <button
                            key={amt}
                            type="button"
                            onClick={() => {
                              setCustomAmountRaw(String(amt));
                              setCustomAmountTouched(true);
                            }}
                            className={`rounded-lg border px-3.5 py-2.5 text-sm font-semibold tabular-nums transition-colors ${
                              active
                                ? "border-primary bg-[var(--brand-primary-soft)] text-primary"
                                : "border-[rgba(12,12,10,0.12)] bg-white text-warm-black hover:border-[rgba(12,12,10,0.25)]"
                            }`}
                          >
                            {formatRupiah(amt)}
                          </button>
                        );
                      })}
                    </div>
                    <div
                      className={`flex items-center gap-2 rounded-lg border bg-white px-3.5 py-3 transition-colors ${
                        pwywError ? "border-red-300" : "border-[rgba(12,12,10,0.12)] focus-within:border-primary"
                      }`}
                    >
                      <span className="text-base font-semibold" style={{ color: "rgba(12,12,10,0.62)" }}>
                        Rp
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={customAmountRaw ? customAmountRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : ""}
                        onChange={(e) => {
                          setCustomAmountRaw(e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 10));
                          setCustomAmountTouched(true);
                        }}
                        placeholder={String(pwywMin).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        aria-label="Nominal dukungan"
                        className="w-full text-lg font-bold text-warm-black tabular-nums outline-none bg-transparent"
                      />
                    </div>
                    {pwywError ? (
                      <p className="text-xs text-red-500 mt-1.5">{pwywError}</p>
                    ) : (
                      <p className="text-xs mt-1.5" style={{ color: "rgba(12,12,10,0.62)" }}>
                        Minimal {formatRupiah(pwywMin)}, kamu bebas menentukan nominalnya.
                      </p>
                    )}
                  </div>
                )}

                {/* Pilih varian (produk digital dengan paket) — tidak berlaku utk PWYW */}
                {!isPwyw && variants.length > 0 && (
                  <div className="mt-4">
                    <p
                      className="text-[11px] font-semibold tracking-widest uppercase mb-2"
                      style={{ color: "rgba(12,12,10,0.62)" }}
                    >
                      Pilih Paket
                    </p>
                    <div className="space-y-2">
                      {variants.map((v) => {
                        const active = selectedVariant?.id === v.id;
                        const vHasDiscount = !!(v.compare_at_price && v.compare_at_price > v.price);
                        return (
                          <label
                            key={v.id}
                            className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                              active
                                ? "border-primary bg-[var(--brand-primary-soft)]"
                                : "border-[rgba(12,12,10,0.12)] bg-white hover:border-[rgba(12,12,10,0.25)]"
                            }`}
                          >
                            <input
                              type="radio"
                              name="product-variant"
                              checked={active}
                              onChange={() => setSelectedVariantId(v.id)}
                              className="h-4 w-4 text-primary focus:ring-primary"
                            />
                            <span className="flex-1 text-sm font-medium text-warm-black">
                              {v.name}
                            </span>
                            <span className="shrink-0 text-right">
                              <span className="block text-sm font-semibold text-warm-black">
                                {formatRupiah(v.price)}
                              </span>
                              {vHasDiscount && (
                                <span
                                  className="block text-[11px] line-through"
                                  style={{ color: "rgba(12,12,10,0.62)" }}
                                >
                                  {formatRupiah(v.compare_at_price!)}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Section: Kode Promo (paid only; disembunyikan utk PWYW, produk gratis,
               & plan yg promonya sudah built-in di harga) ── */}
          {!isFree && !isPwyw && !promoCode && effectivePrice > 0 && !purchaseBlocked && (
            <>
              <div className="h-px bg-black/[0.06]" />
              <div className="px-6 py-4">
                {couponState.status === 'valid' ? (
                  /* Chip kupon aktif: kode + hemat + tombol lepas */
                  <div className="flex items-center gap-2.5 rounded-lg border border-green-200 bg-green-50 px-3.5 py-2.5">
                    <Tag className="h-4 w-4 shrink-0 text-green-600" />
                    <p className="min-w-0 flex-1 text-xs text-green-800">
                      <span className="font-mono font-bold">{couponCode}</span> diterapkan — hemat{" "}
                      <span className="font-semibold tabular-nums">{formatRupiah(couponDiscountAmount)}</span>
                    </p>
                    <button
                      onClick={removeCoupon}
                      aria-label="Lepas kode promo"
                      className="shrink-0 rounded-md p-1 text-green-700 hover:bg-green-100 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ) : !couponOpen ? (
                  /* Collapsed: kurangi friksi checkout */
                  <button
                    onClick={() => setCouponOpen(true)}
                    className="flex items-center gap-2 text-xs font-semibold text-primary hover:underline"
                  >
                    <Tag className="h-3.5 w-3.5" />
                    Punya kode promo?
                  </button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="h-4 w-4" style={{ color: "rgba(12,12,10,0.62)" }} />
                      <span
                        className="text-[11px] font-semibold tracking-widest uppercase"
                        style={{ color: "rgba(12,12,10,0.62)" }}
                      >
                        Kode Promo
                      </span>
                    </div>
                    <div className="flex items-stretch gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => {
                          setCouponInput(e.target.value.toUpperCase());
                          if (couponState.status === 'invalid') setCouponState({ status: 'idle' });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && couponInput.trim()) validateCoupon();
                        }}
                        placeholder="Masukkan kode..."
                        aria-label="Kode promo"
                        autoComplete="off"
                        className="control-h flex-1 min-w-0 px-3.5 text-sm rounded-lg border font-mono border-[rgba(12,12,10,0.12)] bg-white text-warm-black outline-none focus:border-primary"
                      />
                      <button
                        onClick={() => validateCoupon()}
                        disabled={!couponInput.trim() || couponState.status === 'loading'}
                        className="control-h flex items-center gap-1 text-sm font-semibold text-warm-black bg-warm-cream hover:bg-[#e6ddc5] transition-colors border border-[rgba(12,12,10,0.08)] px-3.5 rounded-lg whitespace-nowrap disabled:opacity-50"
                      >
                        {couponState.status === 'loading' ? 'Mengecek...' : 'Terapkan'}
                      </button>
                    </div>
                    {couponState.status === 'invalid' && couponState.error && (
                      <p className="text-xs text-red-500 mt-2">{couponState.error}</p>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* ── Section: Data Pemesan ── */}
          <div className="h-px bg-black/[0.06]" />
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4" style={{ color: "rgba(12,12,10,0.62)" }} />
              <span
                className="text-[11px] font-semibold tracking-widest uppercase"
                style={{ color: "rgba(12,12,10,0.62)" }}
              >
                {isLoggedIn === false ? "Buat Akun & Checkout" : "Data Pemesan"}
              </span>
            </div>

            {/* Guest note */}
            {isLoggedIn === false && (
              <p className="text-xs mb-3" style={{ color: "rgba(12,12,10,0.65)" }}>
                Isi data di bawah, akun otomatis dibuat sekaligus checkout.{" "}
                <Link
                  href={`/login?redirect=/order/${productSlug}`}
                  className="text-primary font-semibold"
                >
                  Sudah punya akun?
                </Link>
              </p>
            )}

            {/* Logged-in user */}
            {isLoggedIn && user && (
              <div className="space-y-3">
                <div>
                  <label
                    className="block text-xs font-semibold mb-1.5"
                    style={{ color: "rgba(12,12,10,0.65)" }}
                  >
                    Nama
                  </label>
                  <input type="text" value={user.name} readOnly className={readOnlyInputClass} />
                </div>
                <div>
                  <label
                    className="block text-xs font-semibold mb-1.5"
                    style={{ color: "rgba(12,12,10,0.65)" }}
                  >
                    Email
                  </label>
                  <input type="email" value={user.email} readOnly className={readOnlyInputClass} />
                </div>
                <div>
                  <label
                    className="block text-xs font-semibold mb-1.5"
                    style={{ color: "rgba(12,12,10,0.65)" }}
                  >
                    WhatsApp
                    {needsMobile && !isFree && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {user.phone ? (
                    <input type="tel" value={user.phone} readOnly className={readOnlyInputClass} />
                  ) : !isFree ? (
                    <>
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="08xxxxxxxxxx"
                        className={editableInputClass}
                      />
                      <p className="text-xs mt-1" style={{ color: "rgba(12,12,10,0.62)" }}>
                        Digunakan untuk konfirmasi pembayaran
                      </p>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {/* Guest form */}
            {isLoggedIn === false && (
              <div className="space-y-3">
                <input type="text" name="_hp_website" value={guestForm._hp_website} onChange={(e) => setGuestForm((prev) => ({ ...prev, _hp_website: e.target.value }))} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
                {(
                  [
                    { key: "name", label: "Nama Lengkap", type: "text", placeholder: "John Doe", autoComplete: "name" },
                    { key: "email", label: "Email", type: "email", placeholder: "nama@email.com", autoComplete: "email" },
                    { key: "phone", label: "WhatsApp", type: "tel", placeholder: "08xxxxxxxxxx", autoComplete: "tel" },
                  ] as const
                ).map((f) => (
                  <div key={f.key}>
                    <label
                      className="block text-xs font-semibold mb-1.5"
                      style={{ color: "rgba(12,12,10,0.65)" }}
                    >
                      {f.label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type={f.type}
                      autoComplete={f.autoComplete}
                      value={guestForm[f.key]}
                      onChange={(e) => {
                        setGuestForm((prev) => ({ ...prev, [f.key]: e.target.value }));
                        if (isDuplicate) {
                          setIsDuplicate(false);
                          setDuplicateField(null);
                        }
                      }}
                      placeholder={f.placeholder}
                      className={editableInputClass}
                    />
                  </div>
                ))}

                <div>
                  <label
                    className="block text-xs font-semibold mb-1.5"
                    style={{ color: "rgba(12,12,10,0.65)" }}
                  >
                    Password <span style={{ color: "rgba(12,12,10,0.62)" }}>(opsional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={guestForm.password}
                      onChange={(e) =>
                        setGuestForm((prev) => ({ ...prev, password: e.target.value }))
                      }
                      placeholder="Kosongkan untuk checkout tanpa password"
                      className={`${editableInputClass} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-lg transition-colors"
                      style={{ color: "rgba(12,12,10,0.62)" }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: "rgba(12,12,10,0.62)" }}>
                    Tanpa password, kamu tetap bisa masuk kapan saja lewat link login yang dikirim ke email.
                  </p>
                </div>

                {guestForm.password && (
                  <div>
                    <label
                      className="block text-xs font-semibold mb-1.5"
                      style={{ color: "rgba(12,12,10,0.65)" }}
                    >
                      Ulangi Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Ulangi password"
                        className={`${editableInputClass} pr-10`}
                      />
                    </div>
                    {confirmPassword && guestForm.password !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Password tidak cocok</p>
                    )}
                  </div>
                )}
                {turnstile.widget}
              </div>
            )}
          </div>

          {/* ── Section: Order Bump (funnel-driven) ── */}
          {!isFree && bump && (
            <>
              <div className="h-px bg-black/[0.06]" />
              <div className="px-6 py-4">
                <FunnelBumpCard bump={bump} selected={bumpSelected} onChange={setBumpSelected} />
              </div>
            </>
          )}

          {/* Duplicate account warning */}
          {isDuplicate && isLoggedIn === false && (
            <div className="mx-5 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3.5">
              <p className="text-xs font-semibold text-amber-800 mb-0.5">Akun sudah terdaftar</p>
              <p className="text-[11px] text-amber-700 mb-2.5">
                {duplicateField === "phone"
                  ? "Nomor WhatsApp ini sudah digunakan oleh akun lain."
                  : `Email ini sudah terdaftar di ${brand_name}.`}
              </p>
              <div className="flex gap-2">
                <Link
                  href={`/login?redirect=/order/${productSlug}`}
                  className="flex-1 text-center py-1.5 bg-[var(--brand-primary)] text-[var(--brand-on-primary,#fff)] text-[11px] font-semibold rounded-md hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  Masuk
                </Link>
                <Link
                  href="/forgot-password"
                  className="flex-1 text-center py-1.5 border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-[11px] font-semibold rounded-md hover:bg-[var(--brand-primary-soft)] transition-colors"
                >
                  Lupa Kata Sandi
                </Link>
              </div>
            </div>
          )}

          {/* ── Section: Metode Pembayaran (paid only; disembunyikan bila total 0) ── */}
          {!isFree && !totalIsZero && !purchaseBlocked && (
            <>
              <div className="h-px bg-black/[0.06]" />
              <div className="px-6 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="h-4 w-4" style={{ color: "rgba(12,12,10,0.62)" }} />
                  <span
                    className="text-[11px] font-semibold tracking-widest uppercase"
                    style={{ color: "rgba(12,12,10,0.62)" }}
                  >
                    Metode Pembayaran
                  </span>
                </div>
                <div className="space-y-2">
                  {PAYMENT_METHODS.map((m) => (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                        paymentMethod === m.id
                          ? "border-primary bg-[var(--brand-primary-soft)]"
                          : "border-[rgba(12,12,10,0.12)] bg-white hover:border-[rgba(12,12,10,0.25)]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        checked={paymentMethod === m.id}
                        onChange={() => setPaymentMethod(m.id)}
                        className="h-4 w-4 text-primary focus:ring-primary"
                      />
                      <span className="flex-1 text-sm font-medium text-warm-black">{m.label}</span>
                      {recommendedMethod(product.type, product.slug || productSlug) === m.id && (
                        <span className="shrink-0 rounded-md bg-[var(--brand-primary-soft)] border border-primary/30 px-2 py-0.5 text-[11px] font-bold text-primary">
                          Direkomendasikan
                        </span>
                      )}
                    </label>
                  ))}
                </div>
                <p className="text-xs mt-2.5" style={{ color: "rgba(12,12,10,0.62)" }}>
                  Pembayaran diproses di halaman {brand_name}, tanpa pindah ke situs lain.
                </p>
              </div>
            </>
          )}

          {/* ── Section: Ringkasan Harga (paid only; total 0 → tanpa baris biaya) ── */}
          {!isFree && !totalIsZero && !purchaseBlocked && (
            <>
              <div className="h-px bg-black/[0.06]" />
              <div className="px-6 py-4">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-4 w-4" style={{ color: "rgba(12,12,10,0.62)" }} />
                  <span
                    className="text-[11px] font-semibold tracking-widest uppercase"
                    style={{ color: "rgba(12,12,10,0.62)" }}
                  >
                    Ringkasan Harga
                  </span>
                </div>
                <div className="space-y-1.5 text-sm">
                  {/* Baris harga dasar SELALU tampil. Sebelumnya hanya muncul saat
                      hasDiscount, jadi produk tanpa diskon yang dipasangi kupon
                      menampilkan daftar potongan tanpa angka acuan sama sekali. */}
                  {!hasDiscount && !isFree && !totalIsZero && (
                    <div className="flex justify-between">
                      <span style={{ color: "rgba(12,12,10,0.65)" }}>Harga produk</span>
                      <span className="text-warm-black">{formatRupiah(effectivePrice)}</span>
                    </div>
                  )}
                  {hasDiscount && (
                    <div className="flex justify-between">
                      <span style={{ color: "rgba(12,12,10,0.65)" }}>Harga normal</span>
                      <span
                        className="line-through"
                        style={{ color: "rgba(12,12,10,0.62)" }}
                      >
                        {formatRupiah(effectiveCompareAt!)}
                      </span>
                    </div>
                  )}
                  {hasDiscount && (
                    <div className="flex justify-between">
                      <span style={{ color: "rgba(12,12,10,0.65)" }}>
                        {promoCode ? (
                          <>Promo <span className="font-mono font-semibold" style={{ color: "rgba(12,12,10,0.65)" }}>{promoCode}</span> (−{discountPct}%)</>
                        ) : "Diskon Reguler"}
                      </span>
                      <span className="text-green-600 font-semibold">
                        − {formatRupiah(effectiveCompareAt! - effectivePrice)}
                      </span>
                    </div>
                  )}
                  {couponState.status === 'valid' && (
                    <div className="flex justify-between">
                      <span style={{ color: "rgba(12,12,10,0.65)" }}>
                        Kupon <span className="font-mono font-semibold" style={{ color: "rgba(12,12,10,0.65)" }}>"{couponCode}"</span>
                      </span>
                      <span className="text-emerald-600 font-semibold">
                        − {formatRupiah(couponDiscountAmount)}
                      </span>
                    </div>
                  )}
                  {bump && bumpSelected && (
                    <div className="flex justify-between">
                      <span style={{ color: "rgba(12,12,10,0.65)" }}>{bump.label}</span>
                      <span className="font-semibold text-warm-black">{formatRupiah(bump.amount)}</span>
                    </div>
                  )}
                  {serviceFee > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: "rgba(12,12,10,0.65)" }}>Biaya layanan</span>
                      <span className="text-warm-black">{formatRupiah(serviceFee)}</span>
                    </div>
                  )}
                  {/* Baris Total — ringkasan sebelumnya berhenti di daftar potongan
                      tanpa pernah menyebut angka akhir; halaman checkout sudah benar. */}
                  {!isFree && !totalIsZero && (
                    <>
                      <div className="h-px bg-black/[0.06] my-2" />
                      <div className="flex justify-between items-baseline">
                        <span className="font-semibold text-warm-black">Total</span>
                        <span className="text-base font-bold text-warm-black tabular-nums">
                          ± {formatRupiah(displayTotal + PAYMENT_ADMIN_FEE_MIN)}
                        </span>
                      </div>
                      {/* Kode unik dibangkitkan acak Rp2.000–2.500 di server saat order
                          dibuat (untuk rekonsiliasi transfer), jadi angka pastinya memang
                          belum bisa diketahui di sini. Yang penting: pembeli tidak lagi
                          menemukan total yang lebih besar setelah menekan Bayar. */}
                      <p className="text-[11px] pt-0.5" style={{ color: "rgba(12,12,10,0.62)" }}>
                        Termasuk kode unik {formatRupiah(PAYMENT_ADMIN_FEE_MIN)}–{formatRupiah(PAYMENT_ADMIN_FEE_MAX)}
                        {" "}untuk verifikasi pembayaran. Nominal persisnya muncul di halaman pembayaran.
                      </p>
                    </>
                  )}
                  {durationLabel[productSlug] && (
                    <p
                      className="text-xs pt-0.5"
                      style={{ color: "rgba(12,12,10,0.62)" }}
                    >
                      {durationLabel[productSlug]}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Error — di-scroll ke tampilan saat muncul (CTA ada di sticky bar
              bawah; tanpa ini pesan bisa di luar layar saat submit dari HP). */}
          {error && (
            <div ref={errorRef} className="mx-5 mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* ── Footer: Total + CTA ── */}
        <div className="bg-white rounded-2xl border border-[rgba(12,12,10,0.08)] shadow-sm px-5 py-3.5">
          <div className="flex items-center justify-between gap-4">
            <div>
              {isFree || totalIsZero ? (
                <p className="text-xl font-bold text-green-700">Gratis</p>
              ) : (
                <>
                  <p className="text-xl font-bold text-warm-black tabular-nums">{formatRupiah(displayTotal)}</p>
                  {hasDiscount && (
                    <p
                      className="text-xs line-through tabular-nums"
                      style={{ color: "rgba(12,12,10,0.62)" }}
                    >
                      {formatRupiah(effectiveCompareAt!)}
                    </p>
                  )}
                </>
              )}
            </div>
            {/* Di bawah md, CTA-nya dipegang sticky bar bawah (thumb zone) — tombol
                inline ini disembunyikan supaya tidak muncul dua tombol identik. */}
            <button
              onClick={primaryAction}
              disabled={primaryDisabled}
              className="min-h-[44px] py-2.5 px-6 bg-primary text-[var(--brand-on-primary,#fff)] text-sm font-semibold rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed hidden md:flex items-center gap-2 shrink-0"
            >
              {ordering ? (
                <>
                  {ctaSpinner}
                  Memproses...
                </>
              ) : (
                ctaLabel
              )}
            </button>
          </div>
          {!purchaseBlocked && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 border-t border-black/[0.05] pt-3 text-[11px] font-medium" style={{ color: "rgba(12,12,10,0.65)" }}>
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[var(--brand-primary)]" /> Pembayaran aman</span>
              <span className="inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-[var(--brand-primary)]" /> Akses instan</span>
              <span className="inline-flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5 text-[var(--brand-primary)]" /> Terverifikasi</span>
            </div>
          )}
          {purchaseBlocked && (
            <p className="mt-2 text-xs" style={{ color: "rgba(12,12,10,0.65)" }}>
              {blockedReason}
            </p>
          )}
          {!purchaseBlocked && isPwyw && pwywInvalid && customAmountTouched && (
            <p className="mt-2 text-xs text-red-500">
              Isi nominal minimal {formatRupiah(pwywMin)} untuk melanjutkan.
            </p>
          )}
          {!purchaseBlocked && (isFree || totalIsZero) && isLoggedIn === false && (
            <p className="mt-2 text-xs" style={{ color: "rgba(12,12,10,0.65)" }}>
              Akses dikirim ke email kamu.
            </p>
          )}
        </div>

        <p
          className="text-center text-sm"
          style={{ color: "rgba(12,12,10,0.62)" }}
        >
          <Link href="/dashboard" className="hover:text-primary transition-colors">
            ← Kembali ke Dashboard
          </Link>
        </p>
      </div>

      {/* ── Sticky pay bar (mobile): CTA uang selalu terlihat tanpa scroll ke bawah ── */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[rgba(12,12,10,0.08)] bg-white/95 backdrop-blur px-4 pt-2.5 pb-[calc(0.7rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(0,0,0,0.06)] md:hidden">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-medium leading-none" style={{ color: "rgba(12,12,10,0.65)" }}>
              {/* Di mobile, bar ini satu-satunya angka yang dilihat saat menekan CTA —
                  ringkasan harga ada jauh di atas. Labelnya harus jujur bahwa masih
                  ada kode unik yang ditambahkan di halaman pembayaran. */}
              {isFree || totalIsZero ? "Total" : "Total + kode unik"}
            </p>
            {isFree || totalIsZero ? (
              <p className="text-lg font-bold text-green-700 leading-tight">Gratis</p>
            ) : (
              <p className="truncate text-lg font-bold text-warm-black tabular-nums leading-tight">
                {formatRupiah(displayTotal)}
              </p>
            )}
          </div>
          <button
            onClick={primaryAction}
            disabled={primaryDisabled}
            className="min-h-[44px] shrink-0 py-2.5 px-5 bg-primary text-[var(--brand-on-primary,#fff)] text-sm font-semibold rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {ordering ? (
              <>
                {ctaSpinner}
                Memproses...
              </>
            ) : (
              ctaLabel
            )}
          </button>
        </div>
      </div>
    </main>
  );
}
